import { PauseIcon } from '@heroicons/react/solid';
import React, { useEffect, useRef, useState } from 'react';
import Image from './Image';
import playAnimation from '/public/assets/play.json';
import Lottie from 'lottie-react';
import { triggerCustomEvent } from '@helpers/miscelleanous';
import useIntersection from '@ui/hooks/useIntersection';

const Video = ({
    // required
    src,
    // optional
    controls = true,
    className = '',
    controlslist = '',
    placeholder = '',
    canPauseOnScroll = false,
    scrollTopOnPause = 500,
    customDefaultPlayVisible = true,
    disablePictureInPicture = true,
    customTimeLine = false,
    customPlayForCover = false,
    customPlayAnimationForCover = false,
    styles = {},
    customControls = false,
    mainWrapperClassName = '',
    coverPlayGtmTagId = '',
    videoPlayGtmTagId = '',
    customEventNameForCoverPlay,
    customEventNameForPause,
    customEventNameForCompletion,
    playVideoOuter = false,
    watchVideoPlayButtonVisible = false,
    watchVideoButtonClassName = 'bg-gray-900 opacity-60 text-xs xl:text-sm cursor-pointer font-semibold px-5 py-3 rounded-full text-basicWhite flex gap-2 items-center justify-center',
    buttonWrapperClassName = '',
    onToggle = () => { },
    ...otherProps
}) => {
    const [playing, setPlaying] = useState(false);
    const [hovered, setHovered] = useState(false);
    const [loading, setLoading] = useState(false);
    const [scrollTop, setScrollTop] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(null);
    const ref = useRef(null);
    const currentTimePercent = (currentTime / duration) * 100; 
    const playIsVisible = useIntersection(ref, '-40px');

    const onPlay = () => {
        setPlaying(true);
        setHovered(false);
        setLoading(false);
        ref.current.play();
        if (!controls && customEventNameForCoverPlay) {
            triggerCustomEvent(customEventNameForCoverPlay);
        }
    };

    const onPause = () => {
        setPlaying(false);
        setLoading(false);
        ref.current.pause();
    };

    const handleScroll = (e) => {
        if (playing) {
            onPause();
        }
    };

    useEffect(() => {
        const handleScroll = (event) => {
            setScrollTop(window.scrollY);
        };

        window.addEventListener('scroll', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    useEffect(() => {
        if (ref.current) {
            ref.current.addEventListener('loadedmetadata', function () {
                const duration = ref?.current?.duration;
                setDuration(duration);
            });
        }
    }, [ref]);

    useEffect(() => {
        if (scrollTop > scrollTopOnPause && canPauseOnScroll) handleScroll();
    }, [scrollTop]);

    useEffect(() => {
        if (playVideoOuter) onPlay();
    }, [playVideoOuter]);

    const handleTimeUpdate = () => {
        setCurrentTime(ref.current.currentTime);
    };

    const handleTimeSliderChange = (e) => {
        const time = parseFloat(e.target.value);
        setCurrentTime(time);
        ref.current.currentTime = time;
    };

    return (
        <>
            <div className={`${mainWrapperClassName}`}>
                <video
                    className={`cursor-pointer ${className}`}
                    style={styles}
                    controls={controls}
                    controlsList={controlslist}
                    poster={placeholder}
                    ref={ref}
                    disablePictureInPicture={disablePictureInPicture}
                    onWaiting={() => setLoading(true)}
                    onPlaying={() => setLoading(false)}
                    onMouseEnter={() => setHovered(true)}
                    onMouseOut={() => setHovered(false)}
                    onEnded={() => {
                        if (onToggle) onToggle(true);
                        setPlaying(false);
                        setLoading(false);
                        if (customEventNameForCompletion) {
                            triggerCustomEvent(customEventNameForCompletion);
                        }
                    }}
                    onPlay={() => {
                        setPlaying(true);
                        setHovered(false);
                        setLoading(false);
                        if (onToggle) onToggle(false);
                    }}
                    onPause={(e) => {
                        if (e.target?.currentTime != e.target?.duration) {
                            // onPause is called when video ends too
                            // hence handleOnPause if not the end of video
                            setPlaying(false);
                            setLoading(false);
                            if (customEventNameForPause) {
                                triggerCustomEvent(customEventNameForPause, { videoDuration: e.target?.currentTime });
                            }
                        }
                    }}
                    onTimeUpdate={handleTimeUpdate}
                    onClick={() => {
                        if (!controls) {
                            if (playing) onPause();
                            else onPlay();
                        }
                    }}
                    {...otherProps}
                >
                    <source src={src} type="video/mp4" />
                    <p>Your browser doesn't support HTML video.</p>
                </video>
            </div>

            {/* Custom play and pause */}
            <button
                className={`absolute left-0 right-0 bottom-1/2 m-auto max-w-max bg-transparent rounded-full opacity-70 shadow z-100 flex justify-center ${customControls ? '' : 'hidden'
                    } ${buttonWrapperClassName}`}
                onMouseEnter={() => setHovered(true)}
            >
                {/* show pause icon when play and hovered are true and loading is false */}
                {playing && hovered && !loading ? (
                    <PauseIcon
                        className="w-8 h-8 lg:w-14 lg:h-14 rounded-full bg-transparent"
                        onClick={onPause}
                    />
                ) : customPlayForCover && !playing && !loading ? (
                    <Image
                        src="/images/play.svg"
                        alt="play"
                        id={videoPlayGtmTagId}
                        onClick={onPlay}
                        className="w-8 h-8 lg:w-14 lg:h-14 bg-transparent cursor-pointer text-primary-700"
                    />
                ) : customPlayAnimationForCover && !playing && !loading && playIsVisible ?
                    <div className='w-16 md:w-[100px] h-16 md:h-[100px]' onClick={onPlay}>
                        <Lottie
                            animationData={playAnimation}
                            autoplay={true}
                            loop={true}
                        />
                    </div>
                    : !playing && !loading && customDefaultPlayVisible ? ( // show play icon when playing and loading both are false
                        watchVideoPlayButtonVisible ? (
                            <span
                                className={watchVideoButtonClassName}
                                id={coverPlayGtmTagId}
                                onClick={onPlay}
                            >
                                <Image
                                    src="/images/homepage/watch-video-play-icon.svg"
                                    aly="play"
                                    className="w-4"
                                />
                                Watch Video
                            </span>
                        ) : (
                            <Image
                                src="/images/homepage/video-play.svg"
                                alt="play"
                                id={coverPlayGtmTagId}
                                onClick={onPlay}
                                className="w-8 bg-transparent cursor-pointer text-primary-700"
                            />
                        )
                    ) : null}
            </button>

            <div
                className={`absolute bottom-0 w-full ${!controls && customTimeLine ? '' : 'hidden'
                    }`}
            >
                <input
                    type="range"
                    min="0"
                    max={ref.current ? ref.current.duration : 0}
                    step="0.1"
                    value={currentTime}
                    onChange={handleTimeSliderChange}
                    className="appearance-none w-full h-2 bg-gray-300 rounded-lg outline-none"
                    style={{
                        backgroundImage: `linear-gradient(to right, #fcd34d 0%, #fcd34d ${currentTimePercent}%, #999 ${currentTimePercent}%, #999 100%)`,
                    }}
                />
            </div>
        </>
    );
};

export default Video;
